/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Mock API URL Router
 * URL 패턴을 매칭하여 적절한 mock handler를 호출합니다.
 */
import type { AxiosResponse } from 'axios';

import type { HttpMethod, MockAxiosRequestConfig, MockRoutePattern } from './types';

type RouteMap = Map<HttpMethod, MockRoutePattern[]>;

const MOCK_BASE_PATH_PREFIXES = ['/backend/api/v1', '/catalog/api/v1', '/connector/api', '/connector'] as const;

export class MockRouter {
  private routes: RouteMap = new Map();

  constructor() {
    this.routes.set('GET', []);
    this.routes.set('POST', []);
    this.routes.set('PATCH', []);
    this.routes.set('PUT', []);
    this.routes.set('DELETE', []);
  }

  /**
   * 라우트 등록
   */
  register(method: HttpMethod, pattern: string | RegExp, handler: MockRoutePattern['handler']): void {
    const regex = typeof pattern === 'string' ? this.patternToRegex(pattern) : pattern;
    const routes = this.routes.get(method);

    if (!routes) {
      throw new Error(`Unsupported HTTP method: ${method}`);
    }

    routes.push({ pattern: regex, handler });
  }

  /**
   * URL 패턴을 정규식으로 변환
   * 예: '/tables/name/:fqn' -> /^\/tables\/name\/([^/]+)$/
   */
  private patternToRegex(pattern: string): RegExp {
    const escaped = pattern.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const withParams = escaped.replace(/:(\w+)/g, '([^/]+)');
    return new RegExp(`^${withParams}$`);
  }

  private normalizeUrl(url: string): string {
    let pathname = url.split('?')[0];

    try {
      pathname = new URL(url, 'http://localhost').pathname;
    } catch {
      // Keep the path parsed above for relative URLs.
    }

    for (const prefix of MOCK_BASE_PATH_PREFIXES) {
      if (pathname === prefix) {
        return '/';
      }

      if (pathname.startsWith(`${prefix}/`)) {
        return pathname.slice(prefix.length);
      }
    }

    return pathname;
  }

  /**
   * URL 매칭 및 handler 실행
   */
  async match(method: HttpMethod, url: string, config?: MockAxiosRequestConfig): Promise<AxiosResponse> {
    const routes = this.routes.get(method);
    const normalizedUrl = this.normalizeUrl(url);

    if (!routes) {
      return this.createNotFoundResponse(normalizedUrl);
    }

    for (const route of routes) {
      const match = normalizedUrl.match(route.pattern);
      if (match) {
        try {
          const result = await route.handler(normalizedUrl, config);
          return result;
        } catch (error) {
          console.error(`[MockRouter] Handler error for ${method} ${normalizedUrl}:`, error);
          return this.createErrorResponse(normalizedUrl, error);
        }
      }
    }

    return this.createNotFoundResponse(normalizedUrl);
  }

  private createNotFoundResponse(url: string): AxiosResponse {
    console.warn(`[MockRouter] No mock handler found for: ${url}`);
    return {
      data: { message: `Mock not implemented: ${url}` },
      status: 404,
      statusText: 'Not Found',
      headers: {} as any,
      config: {} as any,
    };
  }

  private createErrorResponse(_url: string, error: unknown): AxiosResponse {
    return {
      data: { message: error instanceof Error ? error.message : 'Unknown error' },
      status: 500,
      statusText: 'Internal Server Error',
      headers: {} as any,
      config: {} as any,
    };
  }

  /**
   * 성공 응답 생성 헬퍼
   */
  static createSuccessResponse<T>(data: T): AxiosResponse<T> {
    return {
      data,
      status: 200,
      statusText: 'OK',
      headers: {
        'content-type': 'application/json',
      } as any,
      config: {} as any,
    };
  }
}
